import { Link, createFileRoute, notFound, useRouter } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { authClient } from '../../../auth/client'
import { Chalkboard } from '../../../components/Chalkboard'
import { Figure } from '../../../components/Figure'
import { SiteFooter } from '../../../components/SiteFooter'
import { SiteHeader } from '../../../components/SiteHeader'
import { LampHeatmap } from '../../../components/profile/LampHeatmap'
import { useLocale } from '../../../components/use-locale'
import { fmt, getDict, intlTag, parseLocale } from '../../../i18n'
import { DEFAULT_AVATAR } from '../../../lib/avatar'
import { formatDuration } from '../../../lib/format'
import { seoHead } from '../../../lib/seo'
import { SITE_ORIGIN } from '../../../lib/site'
import { getProfileFn } from '../../../server-fns/profile'

export const Route = createFileRoute('/{-$locale}/u/$handle')({
  beforeLoad: ({ params }) => {
    if (!parseLocale(params.locale)) throw notFound()
  },
  loader: async ({ params }) => {
    const profile = await getProfileFn({ data: params.handle })
    if (!profile) throw notFound()
    return { profile }
  },
  head: ({ params, loaderData }) => {
    const locale = parseLocale(params.locale) ?? 'ja'
    const t = getDict(locale)
    const p = loaderData?.profile
    if (!p) return { meta: [{ title: t.meta.notFoundTitle }] }
    return seoHead({
      origin: SITE_ORIGIN,
      locale,
      path: `/u/${p.user.handle}`,
      title: fmt(t.meta.profileTitle, { name: p.user.name }),
      description: fmt(t.meta.profileDescription, {
        name: p.user.name,
        total: formatDuration(p.totalSeconds, locale),
      }),
      image: p.user.image ?? undefined,
    })
  },
  component: ProfilePage,
})

function ProfilePage() {
  const { profile: p } = Route.useLoaderData()
  const { t, locale, path } = useLocale()
  const session = authClient.useSession()
  const isMe = (session.data?.user as { handle?: string | null } | undefined)?.handle === p.user.handle
  const router = useRouter()
  const [deleting, setDeleting] = useState<string | null>(null)
  const [deleteFailed, setDeleteFailed] = useState(false)
  const recentHeading = useRef<HTMLHeadingElement>(null)
  const removeLog = async (id: string) => {
    if (!window.confirm(t.profile.deleteConfirm)) return
    setDeleting(id)
    setDeleteFailed(false)
    try {
      const res = await fetch(`/api/logs/${id}`, { method: 'DELETE' })
      // 404 は別のタブなどで消し終えている
      if (!res.ok && res.status !== 404) throw new Error(`delete ${res.status}`)
      // 累計やヒートマップも変わるので、プロフィールごと読み直す
      await router.invalidate()
      // 押したボタンが消えるので、見出しへ戻して読み上げの位置を見失わないようにする
      recentHeading.current?.focus()
    } catch (e) {
      console.error('[profile] レシートを消せませんでした', e)
      setDeleteFailed(true)
    } finally {
      setDeleting(null)
    }
  }
  const since = new Intl.DateTimeFormat(intlTag(locale), {
    year: 'numeric',
    month: 'long',
    timeZone: p.user.timezone,
  }).format(p.user.createdAt)
  const dateFmt = new Intl.DateTimeFormat(intlTag(locale), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: p.user.timezone,
  })
  return (
    <div className="profile-page">
      <SiteHeader variant="solid" />
      <main id="main" className="profile">
        <header className="profile__head">
          <div className="profile__avatar">
            <img
              src={p.user.image ?? DEFAULT_AVATAR}
              alt=""
              width={96}
              height={96}
              referrerPolicy="no-referrer"
            />
          </div>
          <div>
            <h1 className="profile__name">{p.user.name}</h1>
            <p className="profile__handle">@{p.user.handle}</p>
            {p.user.bio && <p className="profile__bio">{p.user.bio}</p>}
            <p className="profile__since">{fmt(t.profile.since, { date: since })}</p>
            {isMe && (
              <Link to={path('/me')} className="account__link profile__edit">
                {t.account.editName}
              </Link>
            )}
          </div>
        </header>

        <Chalkboard className="profile__card">
          <dl className="profile__stats">
            <div>
              <dt>{t.profile.totalTime}</dt>
              <dd>
                <Figure value={formatDuration(p.totalSeconds, locale)} />
              </dd>
            </div>
            <div>
              <dt>{t.profile.streak}</dt>
              <dd>
                <Figure value={fmt(t.profile.streakDays, { n: p.streak })} />
              </dd>
            </div>
            <div>
              <dt>{t.profile.sessions}</dt>
              <dd>
                <Figure value={fmt(t.profile.sessionsCount, { n: p.totalSessions })} />
              </dd>
            </div>
          </dl>
          <hr className="chalk-rule" />
          <h2 className="profile__subhead">{t.profile.heatmapTitle}</h2>
          <LampHeatmap grid={p.heatmap} />
        </Chalkboard>

        <section className="profile__recent" aria-labelledby="recent-title">
          <h2 id="recent-title" className="lp-heading" ref={recentHeading} tabIndex={-1}>
            {t.profile.recentTitle}
          </h2>
          {deleteFailed && (
            <p className="lp-body" role="alert">
              {t.profile.deleteFailed}
            </p>
          )}
          {p.recentLogs.length === 0 ? (
            <p className="lp-body">{t.profile.noLogs}</p>
          ) : (
            <ul className="profile__logs">
              {p.recentLogs.map((l) => (
                <li key={l.id}>
                  <Link to={path(`/log/${l.id}`)} className="mini-receipt">
                    <span className="mini-receipt__date">{dateFmt.format(l.endedAt)}</span>
                    <span className="mini-receipt__time">{formatDuration(l.focusSeconds, locale)}</span>
                    {l.note && <span className="mini-receipt__note">{l.note}</span>}
                  </Link>
                  {isMe && (
                    <button
                      type="button"
                      className="profile__delete-log"
                      disabled={deleting !== null}
                      onClick={() => void removeLog(l.id)}
                    >
                      {t.profile.deleteLog}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  )
}
